/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import {
  type InfiniteData,
  type PlaceholderDataFunction,
  type QueryClient,
  type UseInfiniteQueryOptions,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';

type InfiniteQueryContext<
  TQueryFnData,
  TData,
  TQueryKey extends readonly unknown[],
  TPageParam,
> = Parameters<
  Exclude<
    UseInfiniteQueryOptions<TQueryFnData, Error, TData, TQueryKey, TPageParam>['queryFn'],
    undefined | symbol
  >
>[0];

type CreateInfiniteQueryOptions<Data, SelectData, PageParam> = {
  initialPageParam: PageParam;
  getNextPageParam: (lastPage: Data, pages: Data[]) => PageParam | undefined;
  selectInfiniteQueryData?: (data: InfiniteData<Data, PageParam>) => SelectData;
};

type InternalInfiniteQueryOptions<Options> = {
  enabled?: boolean;
  gcTime?: number;
  staleTime?: number;
  retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
  retryDelay?: number | ((retryAttempt: number, error: unknown) => number);
  retryOnMount?: boolean;
  queryOptions: Options;
};

export type BaseMergedData<Data, PageParam> = {
  mergedData: Data;
  pages: Data[];
  pageParams: PageParam[];
  pageNumber: PageParam;
};

export type BaseInfiniteMergeFunction<Data, PageParam> = (
  data: InfiniteData<Data, PageParam>,
) => BaseMergedData<Data, PageParam>;

export type CustomUseInfiniteQueryOptions<
  Data,
  QueryKey extends readonly unknown[] = readonly unknown[],
> = {
  refetchOnMount?: boolean;
  placeholderData?: Data | PlaceholderDataFunction<Data, Error, Data, QueryKey>;
};

export function createInfiniteQuery<Data, Options, PageParam, SelectData>(
  key: readonly string[],
  get: (
    options: Options,
    page: PageParam,
    axiosInstance?: AxiosInstance,
    url?: string,
  ) => Promise<Data>,
  createQueryOptions: CreateInfiniteQueryOptions<Data, SelectData, PageParam>,
) {
  const { initialPageParam, getNextPageParam, selectInfiniteQueryData } = createQueryOptions;

  const query = {
    getPrimaryKey: () => key,
    getFullKey: (options: Options) => [...key, options, 'infinite'] as const,
  };

  const queryFn = async (
    {
      queryKey,
      pageParam,
    }: InfiniteQueryContext<Data, SelectData, ReturnType<typeof query.getFullKey>, PageParam>,
    axiosInstance?: AxiosInstance,
  ) => {
    const options = queryKey[queryKey.length - 2] as Options;
    return get({ ...options }, pageParam as PageParam, axiosInstance);
  };

  function useData(
    options: InternalInfiniteQueryOptions<Options> & CustomUseInfiniteQueryOptions<Data>,
  ) {
    const {
      enabled,
      placeholderData,
      gcTime,
      staleTime,
      retry,
      retryDelay,
      refetchOnMount = false,
      queryOptions,
    } = options || {};

    const optionsWithoutUndefined = Object.entries({
      placeholderData,
    }).reduce<Record<string, unknown>>((accumulator, [key, value]: [string, unknown]) => {
      if (value !== undefined) {
        accumulator[key] = value;
      }
      return accumulator;
    }, {});

    return useInfiniteQuery({
      ...optionsWithoutUndefined,
      enabled,
      gcTime,
      staleTime,
      retry,
      retryDelay,
      queryKey: query.getFullKey(queryOptions),
      queryFn,
      initialPageParam,
      getNextPageParam,
      refetchOnMount,
      select: selectInfiniteQueryData,
    });
  }

  const prefetch = (queryClient: QueryClient, options: Options, axiosInstance: AxiosInstance) => {
    return queryClient.prefetchInfiniteQuery({
      queryKey: query.getFullKey(options),
      queryFn: (ctx) => queryFn(ctx, axiosInstance),
      initialPageParam,
    });
  };

  const getFromCache = (queryClient: QueryClient, options: Options) => {
    return queryClient.getQueryData<InfiniteData<Data>>(query.getFullKey(options));
  };

  return { query, useData, prefetch, getFromCache } as const;
}
